import { z } from "zod";
import type { ShadowAnalyticsEvent } from "./schemas";

const uuid = "[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}";
const publicMetadata = z.object({
  tier: z.enum(["Signal", "Trace", "Echo", "Mirror", "Twin", "SHADOW"]).optional(),
  genre: z.string().max(100).optional(),
  readinessScore: z.number().finite().min(0).max(100).optional(),
  readinessStatus: z.enum(["blocked", "draft", "ready_for_review"]).optional(),
  suggestedDepth: z.enum(["Signal", "Trace", "Echo", "Mirror", "Twin", "SHADOW"]).optional(),
  confidence: z.enum(["low", "medium", "high"]).optional(),
  openGateCount: z.number().int().min(0).max(20).optional()
}).strict();

/** Browser statements are never database ownership or server-observed outcome proof. */
export const PublicAnalyticsRequestSchema = z.object({
  eventName: z.enum(["shadow_landing_view", "shadow_signal_started", "shadow_signal_completed", "signal_capsule_exported",
    "signal_receipt_imported", "release_room_opened", "depth_recommendation_viewed", "upgrade_clicked", "genre_benchmark_view", "benchmark_opted_in"]),
  route: z.enum(["/", "/app", "/signal", "/artists", "/share", "/contact", "/compare", "/privacy", "/terms", "/ip", "/login"]).optional(),
  localReferences: z.object({
    artistRecordId: z.string().regex(new RegExp("^art_" + uuid + "$")),
    runId: z.string().regex(new RegExp("^rev_" + uuid + "$"))
  }).strict().optional(),
  // Old clients supplied these fields. They are ignored, never foreign keys.
  artistId: z.string().max(200).optional(), reviewId: z.string().max(200).optional(),
  metadata: publicMetadata.optional(), createdAt: z.string().datetime().optional()
}).strict();
export type PublicAnalyticsRequest = z.infer<typeof PublicAnalyticsRequestSchema>;

export function normalizePublicAnalytics(input: unknown, observedAt = new Date().toISOString()): ShadowAnalyticsEvent {
  const event = PublicAnalyticsRequestSchema.parse(input);
  return {
    eventName: event.eventName, source: "client_reported_unverified", route: event.route, createdAt: z.string().datetime().parse(observedAt),
    metadata: { ...event.metadata, ...(event.localReferences ? {
      localArtistRecordId: event.localReferences.artistRecordId, localRunId: event.localReferences.runId
    } : {}) }
  };
}

export function buildPublicAnalyticsRequest(eventName: PublicAnalyticsRequest["eventName"], input: Omit<PublicAnalyticsRequest, "eventName" | "createdAt"> = {}): PublicAnalyticsRequest {
  return PublicAnalyticsRequestSchema.parse({ eventName, ...input, createdAt: new Date().toISOString() });
}
